import React, { useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'components/buttons/Button';
import { TextField } from 'components/inputs/TextField.react';

import type { TwoFactorProvider } from '../../types/login';
import { BackToLoginLink } from '../common/BackToLoginLink';
import { ErrorMessage } from '../common/ErrorMessage';

interface VerifyCodeStepProps {
    selectedProvider: TwoFactorProvider | null;
    rememberBrowser: boolean;
    isVerifying: boolean;
    error?: string;
    onVerify: (code: string) => void;
    onRememberBrowserChange: (checked: boolean) => void;
    onBack: () => void;
}

export const VerifyCodeStep = ({
    selectedProvider,
    rememberBrowser,
    isVerifying,
    error,
    onVerify,
    onRememberBrowserChange,
    onBack,
}: VerifyCodeStepProps) => {
    const intl = useIntl();
    const [code, setCode] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (code.trim()) {
            onVerify(code.trim());
        }
    };

    return (
        <div>
            <h1 className="tw-text-xl tw-font-semibold tw-mb-8">
                <FormattedMessage
                    id="login.twoFactor.verify.title"
                    defaultMessage="Enter verification code"
                />
            </h1>
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-24">
                <FormattedMessage
                    id="login.twoFactor.verify.description"
                    defaultMessage="Enter the code sent to your {provider}."
                    values={{
                        provider: selectedProvider ?? '',
                    }}
                />
            </p>

            <form onSubmit={handleSubmit}>
                <TextField
                    name="verificationCode"
                    label={intl.formatMessage({
                        id: 'login.twoFactor.verify.code.label',
                        defaultMessage: 'Verification code',
                    })}
                    placeholderText={intl.formatMessage({
                        id: 'login.twoFactor.verify.code.placeholder',
                        defaultMessage: '6-digit code',
                    })}
                    value={code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                    autoFocus
                    autoComplete="one-time-code"
                    className=""
                />

                <label className="tw-flex tw-items-center tw-gap-8 tw-mt-12 tw-text-s">
                    <input
                        type="checkbox"
                        checked={rememberBrowser}
                        onChange={(e) => onRememberBrowserChange(e.target.checked)}
                        className="tw-h-16 tw-w-16"
                    />
                    <FormattedMessage
                        id="login.twoFactor.verify.rememberBrowser"
                        defaultMessage="Remember this browser"
                    />
                </label>

                <ErrorMessage message={error} />

                <div className="tw-mt-16">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isVerifying}
                        wrapperClasses="tw-w-full"
                    >
                        <FormattedMessage
                            id="login.twoFactor.verify.submit"
                            defaultMessage="Verify"
                        />
                    </Button>
                </div>
            </form>

            <BackToLoginLink onClick={onBack} />
        </div>
    );
};
